export interface WordPressPost {
  id: number;
  slug: string;
  title: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  date: string;
  modified: string;
  author: number;
  featured_media: number;
  categories: number[];
  tags: number[];
  _links: {
    self: Array<{ href: string }>;
    author: Array<{ href: string }>;
    'wp:featuredmedia': Array<{ href: string }>;
    'wp:attachment': Array<{ href: string }>;
    'wp:term': Array<{ taxonomy: string; href: string }>;
  };
}

export interface WordPressCategory {
  id: number;
  name: string;
  slug: string;
  description: string;
  count: number;
}

export interface WordPressAuthor {
  id: number;
  name: string;
  description: string;
  slug: string;
  avatar_urls: {
    [key: string]: string;
  };
}

export interface WordPressMedia {
  id: number;
  source_url: string;
  alt_text: string;
  media_details: {
    width: number;
    height: number;
    sizes: {
      [key: string]: {
        source_url: string;
        width: number;
        height: number;
      };
    };
  };
}

export interface WordPressComment {
  id: number;
  author_name: string;
  author_email: string;
  date: string;
  content: {
    rendered: string;
  };
  parent: number;
  post: number;
}

const BASE_URL = 'https://brianf275.sg-host.com/wp-json/wp/v2';
const DOCS_BASE_URL = 'https://brianf275.sg-host.com/wp-json/wp/v2/docs';
const DOC_CATEGORY_URL = 'https://brianf275.sg-host.com/wp-json/wp/v2/doc_category';

class WordPressService {
  async fetchPosts(params: {
    page?: number;
    per_page?: number;
    search?: string;
    categories?: string;
    doc_category?: string;
    orderby?: string;
    order?: 'asc' | 'desc';
  } = {}): Promise<{ posts: WordPressPost[]; totalPages: number; total: number }> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${DOCS_BASE_URL}?${searchParams}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch docs: ${response.statusText}`);
    }

    const posts = await response.json();
    const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1');
    const total = parseInt(response.headers.get('X-WP-Total') || '0');

    return { posts, totalPages, total };
  }

  async fetchPost(slug: string): Promise<WordPressPost> {
    const response = await fetch(`${DOCS_BASE_URL}?slug=${slug}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch doc: ${response.statusText}`);
    }

    const posts = await response.json();
    
    if (!posts.length) {
      throw new Error('Doc not found');
    }

    return posts[0];
  }

  async fetchCategories(): Promise<WordPressCategory[]> {
    try {
      const response = await fetch(`${DOC_CATEGORY_URL}?per_page=100`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch doc categories: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.warn('Failed to fetch BetterDocs categories, using static fallback:', error);
      // Fallback to static data if BetterDocs categories endpoint fails
      return [
        {
          id: 1,
          name: "Getting Started",
          slug: "getting-started",
          description: "Essential guides to help you begin your SeniorThrive journey with confidence",
          count: 0
        },
        {
          id: 2,
          name: "Home Safety Checks",
          slug: "thrivevision-room-scans",
          description: "Learn how to use smart home safety checks to identify and prevent fall risks",
          count: 0
        },
        {
          id: 3,
          name: "Health & Wellness Tracking",
          slug: "health-wellness-tracking",
          description: "Monitor your health metrics and stay on top of your wellness goals",
          count: 0
        },
        {
          id: 4,
          name: "ThriveCircle Family Coordination",
          slug: "thrivecircle-family-coordination",
          description: "Connect with family and coordinate care seamlessly through your support network",
          count: 0
        },
        {
          id: 5,
          name: "Chat Companions & Guide",
          slug: "chat-companions-guide",
          description: "Get personalized support and guidance from your wellness chat companion",
          count: 0
        },
        {
          id: 6,
          name: "Caregiver Resources",
          slug: "caregiver-resources",
          description: "Tools and guidance for family caregivers and professional care providers",
          count: 0
        },
        {
          id: 7,
          name: "Privacy & Permissions",
          slug: "privacy-permissions",
          description: "Understand how your data is protected and manage your privacy settings",
          count: 0
        },
        {
          id: 8,
          name: "Emergency Support",
          slug: "emergency-support",
          description: "Know what to do in emergencies and how to get immediate help when needed",
          count: 0
        }
      ];
    }
  }

  async fetchAuthor(id: number): Promise<WordPressAuthor> {
    const response = await fetch(`${BASE_URL}/users/${id}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch author: ${response.statusText}`);
    }

    return response.json();
  }

  async fetchMedia(id: number): Promise<WordPressMedia> {
    const response = await fetch(`${BASE_URL}/media/${id}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.statusText}`);
    }

    return response.json();
  }

  // Helper method to get featured image URL
  async getFeaturedImageUrl(post: WordPressPost): Promise<string | null> {
    if (!post.featured_media) return null;
    
    try {
      const media = await this.fetchMedia(post.featured_media);
      return media.source_url;
    } catch (error) {
      console.warn('Failed to fetch featured image:', error);
      return null;
    }
  }

  async fetchComments(postId: number): Promise<WordPressComment[]> {
    const response = await fetch(`${BASE_URL}/comments?post=${postId}&per_page=100`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch comments: ${response.statusText}`);
    }

    return response.json();
  }

  // Helper method to strip HTML from excerpt
  stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}

export const wordpressService = new WordPressService();
